import { useCallback, useMemo, useState } from 'react';

import { debounce } from 'lodash';
import { ChatLoading } from 'modules/account/copilot/components';
import { useGetConversations } from 'modules/account/copilot/hooks';
import { FaChevronLeft } from 'react-icons/fa6';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { CreateIcon, SearchIcon, SidebarIcon } from 'ui/components/icon';
import { cn, groupConversations } from 'utils/helpers';
import useCopilotContext from 'utils/providers/useCopilotContext';
import { Conversation } from 'utils/types';

import Tooltip from '../Tooltip';
import HistoryItem from './HistoryItem';

const ChatHistory = () => {
  const [query, setQuery] = useState('');

  const { setSelectedConversation, toggleMode } = useCopilotContext();
  const [searchActive, setSearchActive] = useState(false);

  const { data: conversations, isLoading } = useGetConversations({
    keyword: query,
    perPage: 100,
  });

  const selectConversation = useCallback(
    (conversation: Conversation) => {
      setSelectedConversation?.(conversation._id);
      toggleMode?.();
    },
    [setSelectedConversation, toggleMode],
  );

  const convos = useMemo(() => {
    return groupConversations(conversations ?? []);
  }, [conversations]);

  const searchComponent = useMemo(
    () => (
      <div className="p-5">
        <div className="flex justify-between pb-4 border-b border-b-separator-primary">
          <input
            ref={(ref) => ref?.focus()}
            placeholder="Search..."
            onChange={debounce((e) => setQuery(e.target.value), 300)}
            className={cn('focus:outline-none', 'flex-1 text-sm')}
          />
          <IoCloseCircleOutline
            role="button"
            onClick={() => setSearchActive(false)}
            className="text-content w-5 h-5 hover:text-danger-base transition"
          />
        </div>

        {isLoading ? (
          <ChatLoading />
        ) : (
          <div className="h-[calc(100vh-100px)] overflow-y-auto scrollbar-thin py-2">
            {Object.keys(convos).map((k) => (
              <div key={k}>
                <p className="text-[10px] leading-3 font-semibold py-1.5 px-2.5">
                  {k}
                </p>
                {convos?.[k].map((c) => (
                  <HistoryItem
                    conversation={c}
                    onSelectConversation={selectConversation}
                    key={c._id}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    ),
    [convos, isLoading, selectConversation],
  );

  const historyComponent = useMemo(
    () => (
      <div className="p-5">
        <div className="flex justify-between pb-4 border-b border-b-separator-primary">
          <div className="flex gap-[10px] items-center">
            <FaChevronLeft
              role="button"
              onClick={toggleMode}
              className="w-[14px] h-[14px] hover:opacity-70 transition"
            />
            <SidebarIcon className="w-5 h-5 text-primary-base" />
          </div>
          <div className="flex gap-2 items-center">
            <Tooltip title="Search Chat">
              <SearchIcon
                role="button"
                onClick={() => setSearchActive(true)}
                className="cursor-pointer text-content w-5 h-5 hover:text-primary-base transition"
              />
            </Tooltip>
            <Tooltip placement="bottom-start" title="New Chat">
              <CreateIcon
                role="button"
                onClick={toggleMode}
                className="cursor-pointer text-content w-5 h-5 hover:text-primary-base transition"
              />
            </Tooltip>
          </div>
        </div>
        {isLoading ? (
          <ChatLoading />
        ) : (
          <div className="h-[calc(100vh-100px)] overflow-y-auto scrollbar-thin py-2">
            {Object.keys(convos).map((k) => (
              <div key={k}>
                <p className="text-[10px] leading-3 font-semibold py-1.5 px-2.5">
                  {k}
                </p>
                {convos?.[k].map((c) => (
                  <HistoryItem
                    conversation={c}
                    onSelectConversation={selectConversation}
                    key={c._id}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    ),
    [convos, isLoading, selectConversation, toggleMode],
  );

  const componentToShow = useMemo(() => {
    if (!searchActive) {
      return historyComponent;
    }
    return searchComponent;
  }, [historyComponent, searchActive, searchComponent]);

  return <div className="bg-fill-tertiary max-w-md">{componentToShow}</div>;
};

export default ChatHistory;
